import React, { useState } from 'react';
import { Box, Card, CardHeader, CardBody, Heading, Text, Button, Flex, Icon, Badge, useToast, Divider } from '@chakra-ui/react';
import { FiAlertCircle, FiCheck, FiX, FiEdit2 } from 'react-icons/fi';
import FqPaClaimDetailShell from './FqPaClaimDetailShell';

const mockData = {
  type: '835 Denial',
  patient: { first_name: 'Sofia', last_name: 'Ramirez', member_id: 'KF901266480' },
  provider: { name: 'David T. Nguyen, MD', npi: '1902847561' },
  claim: {
    diagnoses: ['E11.9'], // Type 2 diabetes mellitus without complications
    procedures: [
      { cpt: 'T1015', modifiers: [], charge: 186.00 }, // FQHC encounter \u2014 first claim line
      { cpt: '99214', modifiers: [], charge: 0.00 },   // zero-pay tracking line
      { cpt: '83036', modifiers: [], charge: 0.00 }    // HbA1c (tracking line)
    ],
    clinical_note: "Patient presents for routine diabetes follow-up. Blood sugars have been running high. Patient also complains of numbness and tingling in both feet, consistent with diabetic peripheral neuropathy. Will adjust insulin dosage and refer to podiatry."
  }
};

const FqPaDemoMedicalNecessity = () => {
  const [isApproved, setIsApproved] = useState(false);
  const toast = useToast();

  const handleApprove = () => {
    setIsApproved(true);
    toast({
      title: "Diagnosis Added",
      description: "E11.40 has been added to the claim. Claim queued for resubmission.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const rightPanel = (
    <Card borderColor="purple.200" borderWidth="2px" shadow="md">
      <CardHeader bg="purple.50" pb={4}>
        <Flex justify="space-between" align="center">
          <Heading size="md" display="flex" alignItems="center" color="purple.800">
            <Icon as={FiAlertCircle} mr={2} />
            Revedy Action Required
          </Heading>
          <Badge colorScheme="purple" variant="solid">Tier B</Badge>
        </Flex>
      </CardHeader>
      <CardBody>
        <Box mb={4}>
          <Text fontWeight="bold" color="red.600" mb={1}>Denial Reason (CARC 50):</Text>
          <Text fontSize="sm">These are non-covered services because this is not deemed a 'medical necessity' by the payer.</Text>
        </Box>
        
        <Divider my={4} />

        <Box mb={4}>
          <Text fontWeight="bold" color="purple.700" mb={1}>Revedy Analysis:</Text>
          <Text fontSize="sm" mb={2}>
            The claim was billed with E11.9 (Diabetes without complications). However, the clinical note documents diabetic peripheral neuropathy, which supports a higher-specificity code that meets medical necessity under the payer\u2019s clinical policy (Keystone First applies its clinical policy bulletins and OMAP guidance to MA claims).
          </Text>
        </Box>

        <Box mb={4} p={3} bg="gray.50" borderRadius="md" borderLeft="4px solid" borderLeftColor="purple.400">
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>Documented Evidence:</Text>
          <Text fontSize="sm" fontStyle="italic">"...complains of numbness and tingling in both feet, consistent with diabetic peripheral neuropathy."</Text>
        </Box>

        <Box mb={6}>
          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb={1}>Proposed Action:</Text>
          <Text fontSize="sm" fontWeight="medium">Add diagnosis E11.40 (Type 2 diabetes mellitus with diabetic neuropathy, unspecified) and resubmit to Keystone First.</Text>
        </Box>

        <Flex gap={3}>
          <Button 
            flex="1" 
            colorScheme="purple" 
            leftIcon={<FiCheck />} 
            onClick={handleApprove}
            isDisabled={isApproved}
          >
            {isApproved ? 'Approved' : 'Approve & Fix'}
          </Button>
          <Button flex="1" variant="outline" leftIcon={<FiEdit2 />} isDisabled={isApproved}>
            Edit
          </Button>
          <Button variant="ghost" colorScheme="red" isDisabled={isApproved}>
            <Icon as={FiX} />
          </Button>
        </Flex>
      </CardBody>
    </Card>
  );

  return <FqPaClaimDetailShell claimData={mockData} rightPanel={rightPanel} backTo="/fq-pa/835-review" />;
};

export default FqPaDemoMedicalNecessity;